import { useLocale } from "hooks/useLocale"
import { interpolateTranslations } from "hooks/useTranlate/interpolateTranslations"
import { ReactNode, useCallback } from "react"
import { PrimitiveType, useIntl } from "react-intl"

import { IntlKey, isIntlKey } from "@shared/intl"
import { TranslationEntry } from "@shared/translations/translationEntry"

/**
 * Type for react-intl's rich text formatting approach (allowing specific HTML
 * attributes in translations)
 */
type RichTextValues = (chunks: ReactNode[]) => ReactNode

/**
 * A hook that can be used to translate text (either via an intl key or a translation entry).
 * This hooks wraps around lower-level i18n toolings and allows us to use it with our own
 * types.
 */
export const useTranslate = () => {
  const locale = useLocale()
  const { formatMessage } = useIntl()

  return useCallback<{
    (id: IntlKey | TranslationEntry, values?: Record<string, PrimitiveType | ReactNode | RichTextValues>): string
    (id: IntlKey, values?: Record<string, PrimitiveType | ReactNode | RichTextValues>): string
    (id: IntlKey, values?: Record<string, PrimitiveType | ReactNode | RichTextValues>): ReactNode
  }>(
    // We know the values are valid, so we can safely cast them
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (id: string | TranslationEntry, values?: Record<string, any>): string => {
      if (id instanceof TranslationEntry) {
        if (isIntlKey(id.key)) {
          return formatMessage({ id: id.key }, { ...id.data, ...values })
        }
        return interpolateTranslations(id.key[locale] ?? "", { ...id.data, ...values })
      }

      return formatMessage({ id }, values)
    },
    [formatMessage, locale]
  )
}

/** The translate function returned by {@link useTranslate}, for helpers that receive it as an argument. */
export type Translate = ReturnType<typeof useTranslate>
